import { ChatThreadRouteLayout } from "../components/chat/ChatThreadRouteLayout";
import {
  RIGHT_PANEL_FULLSCREEN_MEDIA_QUERY,
  RIGHT_PANEL_OVERLAY_MEDIA_QUERY,
} from "../components/rightPanelLayout";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useSettings } from "../hooks/useSettings";
import type { DiffRouteSearch } from "../diffRouteSearch";
import { useThreadResourceLabels } from "./-useThreadResourceLabels";
import { useCanonicalRouteThreadRef } from "./-useCanonicalRouteThreadRef";
import {
  shouldRenderDiffPanelContent,
  shouldRenderRightPanelSurface,
} from "./-chatThreadRoute.logic";
import { useCloudRuntimeRouteBlockState } from "./ensureActiveRouteCloudRuntime";
import {
  useBrowserThreadRouteState,
  useLocalThreadRouteState,
  useThreadRightPanelState,
  useThreadRouteWiring,
} from "./-chatThreadRouteHooks";

export interface LocalChatThreadRouteViewProps {
  readonly environmentIdParam: string;
  readonly threadIdParam: string;
  readonly search: DiffRouteSearch;
}

export function LocalChatThreadRouteView(props: LocalChatThreadRouteViewProps) {
  const threadRef = useCanonicalRouteThreadRef(props.environmentIdParam, props.threadIdParam);
  const timestampFormat = useSettings((settings) => settings.timestampFormat);
  const { draftThread, routeThreadExists, serverThreadStarted } =
    useLocalThreadRouteState(threadRef);
  const {
    diffOpen,
    activeKind: rightPanel,
    hasSidebarState,
    surfaces: rightPanelSurfaces,
    activeSurfaceId,
    previewTabId,
    rightSidebarOpen,
    isMaximized: rightPanelMaximized,
    currentThreadKey,
    toggleRightPanelMaximized,
  } = useThreadRightPanelState(threadRef, props.search);
  const { browserThreadState, pendingAutoReveal, consumeAutoReveal } =
    useBrowserThreadRouteState(threadRef);
  const rightSidebarResourceLabels = useThreadResourceLabels({
    browserThreadState,
    previewTabId,
    surfaces: rightPanelSurfaces,
    activeSurfaceId,
  });
  const shouldUseRightPanelSheet = useMediaQuery(RIGHT_PANEL_OVERLAY_MEDIA_QUERY);
  const shouldUseRightPanelFullscreen = useMediaQuery(RIGHT_PANEL_FULLSCREEN_MEDIA_QUERY);
  const cloudRouteBlockState = useCloudRuntimeRouteBlockState(props.environmentIdParam);
  const wiring = useThreadRouteWiring({
    threadRef,
    currentThreadKey,
    diffOpen,
    draftPromoted: Boolean(draftThread?.promotedTo),
    environmentIdParam: props.environmentIdParam,
    threadIdParam: props.threadIdParam,
    serverThreadStarted,
    routeThreadExists,
    pendingBrowserPreviewAutoReveal: pendingAutoReveal,
    consumeBrowserPreviewAutoReveal: consumeAutoReveal,
  });

  if (!threadRef) return null;

  return (
    <ChatThreadRouteLayout
      cloudRouteBlockState={cloudRouteBlockState}
      closeRightPanel={wiring.closeRightPanel}
      closeRightPanelSurfaceTab={wiring.closeRightPanelSurfaceTab}
      resourceLabels={rightSidebarResourceLabels}
      markDiffOpened={wiring.markDiffOpened}
      surfaces={rightPanelSurfaces}
      activeSurfaceId={activeSurfaceId}
      activateRightPanelSurface={wiring.activateRightPanelSurface}
      previewTabId={previewTabId}
      rightPanel={rightPanel}
      rightSidebarOpen={rightSidebarOpen}
      rightPanelMaximized={rightPanelMaximized}
      onToggleRightPanelMaximized={toggleRightPanelMaximized}
      selectRightPanel={wiring.selectRightPanel}
      shouldRenderDiffContent={shouldRenderDiffPanelContent({
        rightPanel,
        hasOpenedDiff: wiring.hasOpenedDiff,
      })}
      shouldRenderRightPanel={shouldRenderRightPanelSurface({
        hasSidebarState,
        sidebarOpen: rightSidebarOpen,
      })}
      shouldUseRightPanelFullscreen={shouldUseRightPanelFullscreen}
      shouldUseRightPanelSheet={shouldUseRightPanelSheet}
      threadRef={threadRef}
      timestampFormat={timestampFormat}
    />
  );
}
